import { useCallback, useEffect, useRef } from "react";
import { Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  MAX_COACH_INPUT_CHARS,
  useCoachKeyResults,
  type CoachError,
  type KrTypeInput,
} from "@/hooks/use-coach";
import { CoachFeedbackCard } from "./coach-feedback-card";
import type { KrQuarter, WizardKrDraft } from "./wizard-state";

interface KrEntryCardProps {
  index: number;
  kr: WizardKrDraft;
  objectiveStatement: string;
  onChange: (next: WizardKrDraft) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export function KrEntryCard({
  index,
  kr,
  objectiveStatement,
  onChange,
  onRemove,
  canRemove,
}: KrEntryCardProps) {
  const { feedback, isStreaming, error, analyze, reset } = useCoachKeyResults();

  const trimmedObjective = objectiveStatement.trim();
  const trimmedTitle = kr.title.trim();
  const canCoach =
    !isStreaming && trimmedTitle.length > 0 && trimmedObjective.length > 0;

  const update = (patch: Partial<WizardKrDraft>) => {
    onChange({ ...kr, ...patch });
  };

  const requestCoaching = useCallback(async () => {
    if (!canCoach) return;
    const target = parseTarget(kr.targetValue, kr.type);
    await analyze({
      objective_statement: trimmedObjective,
      key_results: [
        {
          title: trimmedTitle.slice(0, MAX_COACH_INPUT_CHARS),
          type: kr.type,
          target_value: target,
        },
      ],
      mode: "individual",
    });
  }, [analyze, canCoach, kr.targetValue, kr.type, trimmedObjective, trimmedTitle]);

  // Persist streamed feedback into wizard state when stream ends cleanly.
  useCommitFeedbackOnStreamEnd(isStreaming, error, feedback, () => {
    if (kr.coachFeedback !== feedback) {
      onChange({ ...kr, coachFeedback: feedback });
    }
  });

  const handleRetry = () => {
    reset();
    void requestCoaching();
  };

  const displayFeedback = isStreaming ? feedback : feedback || kr.coachFeedback;
  const hasAnyFeedback = displayFeedback.length > 0 || error !== null;

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-start justify-between gap-3">
          <span className="font-heading text-sm font-semibold uppercase tracking-wide text-[hsl(var(--primary))]">
            Key Result {index + 1}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={requestCoaching}
              disabled={!canCoach}
            >
              <Sparkles className="mr-2 h-3.5 w-3.5" />
              Coach This KR
            </Button>
            {canRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRemove}
                aria-label="Remove Key Result"
                className="text-[hsl(var(--muted-foreground))] hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor={`kr-title-${kr.localId}`} className="sr-only">
            Key Result title
          </Label>
          <Textarea
            id={`kr-title-${kr.localId}`}
            rows={2}
            maxLength={MAX_COACH_INPUT_CHARS}
            placeholder="What measurable outcome will prove progress?"
            value={kr.title}
            onChange={(e) =>
              update({ title: e.target.value.slice(0, MAX_COACH_INPUT_CHARS) })
            }
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor={`kr-type-${kr.localId}`}>Type</Label>
            <Select
              value={kr.type}
              onValueChange={(v) => {
                const nextType = v as KrTypeInput;
                const patch: Partial<WizardKrDraft> = { type: nextType };
                if (nextType !== "metric") patch.targetValue = "";
                update(patch);
              }}
            >
              <SelectTrigger id={`kr-type-${kr.localId}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Percentage</SelectItem>
                <SelectItem value="binary">Binary</SelectItem>
                <SelectItem value="metric">Metric</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`kr-target-${kr.localId}`}>Target Value</Label>
            <Input
              id={`kr-target-${kr.localId}`}
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              disabled={kr.type !== "metric"}
              placeholder={kr.type === "metric" ? "e.g., 12" : "N/A"}
              value={kr.targetValue}
              onChange={(e) => update({ targetValue: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`kr-quarter-${kr.localId}`}>Quarter</Label>
            <Select
              value={kr.quarter}
              onValueChange={(v) => update({ quarter: v as KrQuarter })}
            >
              <SelectTrigger id={`kr-quarter-${kr.localId}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Q1">Q1</SelectItem>
                <SelectItem value="Q2">Q2</SelectItem>
                <SelectItem value="Q3">Q3</SelectItem>
                <SelectItem value="Q4">Q4</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`kr-due-${kr.localId}`}>Due Date</Label>
            <Input
              id={`kr-due-${kr.localId}`}
              type="date"
              value={kr.dueDate}
              onChange={(e) => update({ dueDate: e.target.value })}
            />
          </div>
        </div>

        {hasAnyFeedback && (
          <CoachFeedbackCard
            feedback={displayFeedback}
            roundNumber={1}
            isStreaming={isStreaming}
            error={error}
            onRetry={isErrorRetryable(error) ? handleRetry : undefined}
          />
        )}
      </CardContent>
    </Card>
  );
}

function parseTarget(raw: string, type: KrTypeInput): number | null {
  if (type !== "metric") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function isErrorRetryable(error: CoachError | null): boolean {
  if (!error) return false;
  return error.kind !== "aborted";
}

function useCommitFeedbackOnStreamEnd(
  isStreaming: boolean,
  error: CoachError | null,
  feedback: string,
  onCommit: () => void,
) {
  const prev = useRef(isStreaming);
  useEffect(() => {
    if (prev.current && !isStreaming && !error && feedback.length > 0) {
      onCommit();
    }
    prev.current = isStreaming;
  }, [isStreaming, error, feedback, onCommit]);
}
